// Copyright (c) Docugami, Inc. All rights reserved.

import { chunkSelectedBorderColor } from "components/shell/presentation/AppStyles";
import { BoundsComponent } from "sdv/components/BoundsComponent";
import { ChunkSelectorComponent } from "sdv/components/ChunkSelectorComponent";
import { getParentTable, getTokenRange } from "sdv/components/ControlComponent";
import { InteractableComponent, InteractionType } from "sdv/components/InteractableComponent";
import { SelectionContextComponent } from "sdv/components/SelectionContextComponent";
import { SimpleTableColumnHeaderComponent } from "sdv/components/SimpleTableColumnHeaderComponent";
import { SvgComponent } from "sdv/components/SvgComponent";
import { TableChunkSelectorComponent, TableSelectorType } from "sdv/components/TableChunkSelectorComponent";
import { ComponentId, tagComponent } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { ActionId } from "sdv/input/Actions";
import { InputState } from "sdv/input/InputController";
import { SelectionFilters } from "sdv/SelectionFilters";
import { Box, combineBoxes, growBox, pointInBox } from "store/shared_types";
import { inRangeInclusive } from "utils/MathUtils";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";

/**
 * Handles table specific selection logic such as creating the special table selectors
 * and updating visuals when those selectors are hovereed.
 */
export class TableChunkSelectionSystem implements ISystem {
    get updateEnabled() { return true; }

    entities!: IEntityManager;
    resources!: IResources;
    input!: InputState;
    queries: Queries = {
        tableChunks: { types: [ComponentId.TableChunkRender, ComponentId.Control, ComponentId.Bounds, ComponentId.Table] },
        rowChunks: { types: [ComponentId.TableChunkRender, ComponentId.Control, ComponentId.Bounds, ComponentId.TableRow] },
        simpleColumns: { types: [ComponentId.TableChunkRender, ComponentId.SimpleTableColumnHeader, ComponentId.Bounds] },
        simpleColumnRender: { types: [ComponentId.TableChunkRender, ComponentId.SimpleTableColumnHeader, ComponentId.TableColumnRender] },
        activeSelectors: { types: [ComponentId.ChunkSelector, ComponentId.TableChunkSelector, ComponentId.CursorHit] },
        allTableSelectors: { types: [ComponentId.ChunkSelector, ComponentId.TableChunkSelector, ComponentId.Interactable] },
        hoverColumnHeader: { types: [ComponentId.Control, ComponentId.Hover, ComponentId.SimpleTableColumnHeader] },
        selectedControl: { types: [ComponentId.Control, ComponentId.Selected, ComponentId.SimpleTableColumnHeader] },
        hoveredTableChunk: { types: [ComponentId.Hover, ComponentId.TableChunkRender, ComponentId.Control] },
        selectionContextTable: { types: [ComponentId.SelectionContext, ComponentId.Table] },
        resize: { types: [ComponentId.Resize] }
    }

    update() {
        this.createTableSelectors();
        this.createColumnSelectors();
        this.createRowSelectors();
        this.updateTableSelectionContext();
        this.updateColumnSelectorVisuals();
    }

    private createTableSelectors() {
        if (!this.resources.get("pagesLoaded").value) {
            return;
        }

        const selectionFiltersResource = this.resources.get("selectionFilters");
        const selectionFilters = selectionFiltersResource.value;

        // Create table selectors
        const tableSelectorsSupported = selectionFilters === null || !!(selectionFilters & SelectionFilters.Tables);
        for (const chunk of this.queries.tableChunks.query!.entities) {
            const selectorName = getChunkSelectorEntityName(chunk.name);
            let selectorEntity = this.entities.get(selectorName);

            if (!tableSelectorsSupported) {
                selectorEntity?.destroy();
                continue;
            }

            if (!selectorEntity || selectionFiltersResource.updated) {
                const parentTable = getParentTable(chunk, this.entities);
                if (!!parentTable) {
                    const bounds = chunk.getComponent<BoundsComponent>(ComponentId.Bounds)!;
                    let hitBox: Box | undefined;
                    if (bounds.bounds.length > 0) {
                        if (!!selectionFilters && !!(selectionFilters & SelectionFilters.Tables)) {
                            hitBox = { ...bounds.boundingBox };
                        }
                        else {
                            hitBox = createTableSelectorHitBox(bounds);
                        }
                    }

                    if (!!hitBox) {
                        // Create the chunk selector for the table
                        selectorEntity = selectorEntity ?? this.entities.create(selectorName);
                        selectorEntity
                            .ensureComponent<ChunkSelectorComponent>({
                                typeId: ComponentId.ChunkSelector,
                                targetEntity: chunk
                            }, true)
                            .ensureComponent<InteractableComponent>({
                                typeId: ComponentId.Interactable,
                                interactionType: InteractionType.Hover,
                                hitBox: hitBox
                            }, true)
                            .ensureComponent<TableChunkSelectorComponent>({
                                typeId: ComponentId.TableChunkSelector,
                                selectorType: TableSelectorType.Table,
                                parentTableId: parentTable.name,
                                supportedFilters: SelectionFilters.Tables,
                            })
                            .ensureComponent<SvgComponent>({
                                typeId: ComponentId.Svg,
                                point: { x: hitBox.left, y: hitBox.top },
                                paths: [{
                                    path: "M0 -0.5H-0.5V0V50V50.5H0H50H50.5V50V0V-0.5H50H0ZM46.1 15.2792H34.8273V3.90004H46.1V15.2792ZM30.4998 3.90004V15.2792H18.8665V3.90004H30.4998ZM14.5278 15.2792H3.90004V3.90004H14.5278V15.2792ZM3.90004 19.6078H14.5278V31.2412H3.90004V19.6078ZM3.90004 35.5749H14.5278V46.0962H3.90004V35.5749ZM18.8665 35.5749H30.4998V46.0962H18.8665V35.5749ZM18.8665 31.2412V19.6078H30.4998V31.2412H18.8665ZM34.8323 19.6078H46.105V31.2412H34.8323V19.6078ZM34.8323 46.1V35.5786H46.105V46.1H34.8323Z",
                                    color: chunkSelectedBorderColor
                                }],
                                scale: .3
                            })
                    }
                }
            }
        }
    }

    private createColumnSelectors() {
        if (!this.resources.get("pagesLoaded").value) {
            return;
        }

        const selectionFiltersResource = this.resources.get("selectionFilters");
        let updateSelectors = selectionFiltersResource.updated;
        const selectionFilters = selectionFiltersResource.value;

        // Create column selectors
        const columnSelectorsSupported = selectionFilters === null || !!(selectionFilters & SelectionFilters.SimpleColumns);
        for (const column of this.queries.simpleColumns.query!.entities) {
            // Get the selector for this column
            const selectorName = getChunkSelectorEntityName(column.name);
            let selectorEntity = this.entities.get(selectorName);

            // If selection is disabled, destroy the selector
            if (!columnSelectorsSupported) {
                selectorEntity?.destroy();
                continue;
            }

            // If we don't yet have a selector, but they are supported then we should create one
            if (!selectorEntity || updateSelectors) {
                const parentTable = getParentTable(column, this.entities);
                if (!!parentTable) {
                    const columnComp = column.getComponent<SimpleTableColumnHeaderComponent>(ComponentId.SimpleTableColumnHeader)!;
                    const bounds = column.getComponent<BoundsComponent>(ComponentId.Bounds)!;
                    if (!!columnComp.boxes.length && !!bounds.bounds.length) {
                        let hitBox: Box;
                        if (!!selectionFilters && !!(selectionFilters & SelectionFilters.SimpleColumns)) {
                            // Create a selector for the entire column
                            hitBox = combineBoxes(columnComp.boxes);
                        }
                        else {
                            // Create a selector for the header
                            hitBox = createColumnSelectorHitBox(columnComp);
                        }

                        selectorEntity = selectorEntity ?? this.entities.create(selectorName);

                        // Create the chunk selector for the column
                        selectorEntity
                            .ensureComponent<ChunkSelectorComponent>({
                                typeId: ComponentId.ChunkSelector,
                                targetEntity: column
                            }, true)
                            .ensureComponent<InteractableComponent>({
                                typeId: ComponentId.Interactable,
                                interactionType: InteractionType.Hover,
                                hitBox: hitBox
                            }, true)
                            .ensureComponent<TableChunkSelectorComponent>({
                                typeId: ComponentId.TableChunkSelector,
                                selectorType: TableSelectorType.Column,
                                parentTableId: parentTable.name,
                                visibleRangeX: [bounds.bounds[0].left, bounds.bounds[0].right],
                                supportedFilters: SelectionFilters.SimpleColumns
                            })
                            .ensureComponent<SvgComponent>({
                                typeId: ComponentId.Svg,
                                point: { x: (hitBox.left + hitBox.right) / 2 - 3, y: hitBox.top },
                                paths: [{
                                    path: "M0 0V50H19.6429V0H0ZM3.39298 19.1078H16.2499V31.7412H3.39298V19.1078ZM3.39298 35.0749H16.2499V46.5962H3.39298V35.0749ZM16.2499 15.7792H3.39298V3.40004H16.2499V15.7792Z",
                                    color: chunkSelectedBorderColor
                                }],
                                scale: .3
                            })
                    }
                }
            }
        }
    }

    private createRowSelectors() {
        if (!this.resources.get("pagesLoaded").value) {
            return;
        }

        const selectionFiltersResource = this.resources.get("selectionFilters");
        const selectionFilters = selectionFiltersResource.value;

        // Create row selectors
        for (const row of this.queries.rowChunks.query!.entities) {
            // Get the selector for this row
            const selectorName = getChunkSelectorEntityName(row.name);
            let selectorEntity = this.entities.get(selectorName);

            // If selection there is an active filter then we don't use row selectors
            if (selectionFilters !== null) {
                selectorEntity?.destroy();
            }
            else if (!selectorEntity || selectionFiltersResource.updated) {
                const parentTable = getParentTable(row, this.entities);
                const bounds = row.getComponent<BoundsComponent>(ComponentId.Bounds)!;
                if (!!parentTable && !!bounds.bounds.length) {
                    // If we don't yet have a selector, but they are supported then we should create one
                    const hitBox = createRowSelectorHitBox(bounds);

                    selectorEntity = selectorEntity ?? this.entities.create(selectorName);

                    // Create the chunk selector for the row
                    selectorEntity
                        .ensureComponent<ChunkSelectorComponent>({
                            typeId: ComponentId.ChunkSelector,
                            targetEntity: row
                        }, true)
                        .ensureComponent<InteractableComponent>({
                            typeId: ComponentId.Interactable,
                            interactionType: InteractionType.Hover,
                            hitBox: hitBox
                        }, true)
                        .ensureComponent<TableChunkSelectorComponent>({
                            typeId: ComponentId.TableChunkSelector,
                            selectorType: TableSelectorType.Row,
                            visibleRangeY: [bounds.boundingBox.top, bounds.boundingBox.bottom],
                            parentTableId: parentTable.name
                        })
                        .ensureComponent<SvgComponent>({
                            typeId: ComponentId.Svg,
                            point: { x: hitBox.right - 19, y: (hitBox.top + hitBox.bottom) / 2 - 3 },
                            paths: [{
                                path: "M50 0H0V19.6429H50V0ZM30.8921 3.39298V16.2499H18.2588V3.39298H30.8921ZM14.9251 3.39298V16.2499H3.4038V3.39298H14.9251ZM34.2246 16.2499V3.39298H46.6V16.2499H34.2246Z",
                                color: chunkSelectedBorderColor
                            }],
                            scale: .3
                        })
                }
            }
        }
    }

    private updateTableSelectionContext() {
        if (this.resources.get("selectionFilters").value !== null) {
            return;
        }

        const selectionContextTableEntity = this.queries.selectionContextTable.query!.single();
        const selectionContextTableEntityId = selectionContextTableEntity?.name ?? null;

        const hoveredTableChunk = this.queries.hoveredTableChunk.query!.single();
        let parentTable: Entity | null = null;
        let parentTableId: string | null = null;
        if (!!hoveredTableChunk) {
            // Find the root table
            parentTable = getParentTable(hoveredTableChunk, this.entities);
            parentTableId = parentTable?.name ?? null;
        }

        if (parentTableId !== selectionContextTableEntityId) {
            const cursor = this.input.getAxis2d(ActionId.SelectCursor)!;
            if (!!parentTable?.hasComponent(ComponentId.Bounds) && !!cursor) {
                const bounds = parentTable.getComponent<BoundsComponent>(ComponentId.Bounds)!;
                const target = bounds.bounds.find(x => pointInBox(cursor.x, cursor.y, x));
                if (!!target) {
                    selectionContextTableEntity?.removeComponent(ComponentId.SelectionContext);

                    // Add the selection context to this table
                    parentTable.addComponent<SelectionContextComponent>({
                        typeId: ComponentId.SelectionContext,
                        tokenRange: getTokenRange(parentTable)!,
                        cursorZone: growBox(target, 35, 10, 10, 35),
                        exitOnCursorLeave: "remove"
                    });
                }

            }
        }

        this.enableSelectorsForTable(selectionContextTableEntityId);
    }

    private enableSelectorsForTable(tableId: string | null) {
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        const isResize = this.queries.resize.query!.any();
        for (const entity of this.queries.allTableSelectors.query!.entities) {
            const interactableComp = entity.getComponent<InteractableComponent>(ComponentId.Interactable)!;
            const selectorComp = entity.getComponent<TableChunkSelectorComponent>(ComponentId.TableChunkSelector)!;
            let isEnabled = selectorComp.parentTableId === tableId && !isResize;
            if (isEnabled && !!selectorComp.visibleRangeX) {
                isEnabled = !!cursor && inRangeInclusive(cursor.x, selectorComp.visibleRangeX[0], selectorComp.visibleRangeX[1]);
            }

            if (isEnabled && !!selectorComp.visibleRangeY) {
                isEnabled = !!cursor && inRangeInclusive(cursor.y, selectorComp.visibleRangeY[0], selectorComp.visibleRangeY[1]);
            }

            if (isEnabled) {
                entity.ensureComponent(tagComponent(ComponentId.Renderable));
            }
            else {
                entity.removeComponent(ComponentId.Renderable);
            }

            interactableComp.disabled = !isEnabled;
        }
    }

    private updateColumnSelectorVisuals() {
        const selectionFiltersResource = this.resources.get("selectionFilters");
        const renderColumns = !!selectionFiltersResource.value && !!(selectionFiltersResource.value & SelectionFilters.SimpleColumns);
        const selectedEntity = this.queries.selectedControl.query!.single();

        if (renderColumns || selectionFiltersResource.updated) {
            // All simple columns should be rendered as such so we don't even have to do anything special after this
            for (const columnEntity of this.queries.simpleColumns.query!.entities) {
                if (renderColumns) {
                    columnEntity.ensureComponent({ typeId: ComponentId.TableColumnRender });
                }
                else if (columnEntity !== selectedEntity) {
                    columnEntity.removeComponent(ComponentId.TableColumnRender);
                }
            }

            return;
        }

        const selectorTargetEntity = this.queries.activeSelectors.query!.single()?.getComponent<ChunkSelectorComponent>(ComponentId.ChunkSelector)!.targetEntity ?? null;
        const hoverEntity = this.queries.hoverColumnHeader.query!.single();

        let renderAsColumnEntity: Entity | null = null;
        if (!!selectorTargetEntity) {
            if (selectorTargetEntity === hoverEntity) {
                // The hovered entity is the column chunk for the active selector so we should render it as a column
                renderAsColumnEntity = hoverEntity;
            }
            else if (selectorTargetEntity === selectedEntity) {
                renderAsColumnEntity = selectedEntity;
            }
        }

        for (const columnEntity of this.queries.simpleColumnRender.query!.entities) {
            if (columnEntity === selectedEntity || columnEntity === renderAsColumnEntity) {
                continue;
            }

            columnEntity.removeComponent(ComponentId.TableColumnRender);
        }

        if (!!renderAsColumnEntity && !renderAsColumnEntity.hasComponent(ComponentId.TableColumnRender)) {
            renderAsColumnEntity.addComponent(tagComponent(ComponentId.TableColumnRender));
        }
    }
}

const getChunkSelectorEntityName = (chunkId: string) => `selector_${chunkId}`;

function createColumnSelectorHitBox(columnComp: SimpleTableColumnHeaderComponent): Box {
    const targetBox = columnComp.boxes[0]!;
    const left = targetBox.left + ((targetBox.right - targetBox.left) / 2) - 25;
    const top = Math.max(0, targetBox.top - 20);

    return {
        ...targetBox,
        left: left,
        top: top,
        right: left + 50,
        bottom: top + 20
    }
}

function createRowSelectorHitBox(bounds: BoundsComponent): Box {
    const left = Math.max(0, bounds.boundingBox.left - 25);
    return {
        ...bounds.boundingBox,
        left: left,
        right: left + 25,
    }
}

function createTableSelectorHitBox(bounds: BoundsComponent): Box {
    const box = bounds.bounds[0];
    const left = Math.max(0, box.left - 19);
    const top = Math.max(0, box.top - 19);

    return {
        ...box,
        left: left,
        top: top,
        right: left + 19,
        bottom: top + 19
    }
}
